import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Moon, Sun } from 'lucide-react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'
import { useTheme } from '@/components/generals/theme-provider'

const LINKS = [
    { href: '/', label: "Gallery" },
    { href: '/about', label: "About" },
    { href: '/contact', label: "Contact" }
]

export const SiteNav: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const { theme, toggle } = useTheme()

    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    return (
        <header className='site-nav fixed inset-x-0 top-0 z-50 h-20 transition-opacity duration-[450ms] ease-out'>
            <div
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute inset-x-0 -top-4 h-32 bg-gradient-to-b from-surface via-surface/85 to-transparent transition-opacity duration-500',
                    scrolled ? 'opacity-100' : 'opacity-0'
                )}
            />

            <nav
                aria-label='Primary'
                className='relative flex h-full items-center justify-between px-8 sm:px-12'
            >
                <Link
                    href='/'
                    className='group flex items-baseline gap-[0.85rem] text-ink'
                >
                    <span className='text-[1.05rem] font-semibold tracking-[-0.02em]'>
                        Fluid Gallery
                    </span>

                    <span
                        aria-hidden='true'
                        className='size-[5px] translate-y-[-1px] rounded-[1px] bg-flare transition-transform duration-500 group-hover:translate-y-[-5px]'
                    />

                    <span className='meta hidden text-ink-faint sm:inline'>
                        Photography
                    </span>
                </Link>

                <div className='flex items-center gap-6 sm:gap-8'>
                    <ul className='flex items-center gap-5 sm:gap-8'>
                        {LINKS.map(link => {
                            const current = link.href === '/'
                                ? pathname === '/'
                                : pathname.startsWith(link.href)

                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={current ? 'page' : undefined}
                                        className={cn(
                                            'eyebrow relative block py-3 transition-colors duration-300',
                                            current ? 'text-ink' : 'text-ink-faint hover:text-ink'
                                        )}
                                    >
                                        {link.label}

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute inset-x-0 bottom-1.5 h-px origin-left bg-flare transition-transform duration-500 ease-out',
                                                current ? 'scale-x-100' : 'scale-x-0'
                                            )}
                                        />
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <span aria-hidden='true' className='hidden h-4 w-px bg-hairline sm:block' />

                    <button
                        type='button'
                        onClick={toggle}
                        aria-label={theme === 'dark' ? "Switch to light" : "Switch to dark"}
                        className='flex size-9 items-center justify-center rounded-full border border-hairline text-ink-faint transition-colors duration-300 hover:border-flare hover:text-ink'
                    >
                        {theme === 'dark'
                            ? <Sun className='size-4' />
                            : <Moon className='size-4' />}
                    </button>
                </div>
            </nav>
        </header>
    )
}
